import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Search, BookOpen, Clock, ArrowRight } from 'lucide-react';
import { GUIDE_CATEGORIES } from '../data/contentData';
import { useGuides } from '../data/hooks';
import { LoadingState, ErrorState, EmptyState } from '../components/states/AsyncStates';
import SEO from '../components/SEO';
import Img from '../components/Img';

const SourcingGuides = () => {
  const [query, setQuery] = useState('');
  const [activeCat, setActiveCat] = useState('all');

  const { data: GUIDES, loading, error, refetch } = useGuides();
  const list = GUIDES || [];

  const filtered = list.filter(g => {
    if (activeCat !== 'all' && g.category !== activeCat) return false;
    if (query) {
      const q = query.toLowerCase();
      return g.title.toLowerCase().includes(q) || (g.summary || '').toLowerCase().includes(q) || (g.categoryLabel || '').toLowerCase().includes(q);
    }
    return true;
  });

  const featured = list[0];

  return (
    <div data-testid="sourcing-guides-page" className="surface-obsidian text-platinum">
      <SEO
        title="Sourcing Guides — Manufacturing & Factory Intelligence Library"
        description="Editorial intelligence briefs on global fashion sourcing — country sourcing, MOQ negotiation, SMETA audits, OEM vs ODM, factory verification. Curated by GIWAHS editorial intelligence."
        keywords="sourcing guides, factory intelligence, MOQ negotiation, SMETA audit, OEM ODM"
      />
      {/* HERO */}
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline">/ Sourcing Guides</span>
          <h1 className="mt-5 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2.5rem, 7vw, 6.5rem)', lineHeight: 0.92 }}>
            The sourcing<br />intelligence library.
          </h1>
          <p className="mt-7 max-w-2xl text-base leading-relaxed text-neutral-700 md:text-lg">
            Editorial intelligence briefs for buyers and brand operators — how to source, where to source, what to negotiate, and how to verify what you're buying.
          </p>

          {/* Search + filters */}
          <div className="mt-10 flex flex-col gap-4 md:flex-row">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2" size={14} strokeWidth={1.5} />
              <input
                data-testid="guides-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search guides — country, category, topic…"
                className="giwahs-pill-field w-full py-3 pl-10 pr-3 text-sm"
              />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {GUIDE_CATEGORIES.map(c => (
              <button
                key={c.slug}
                onClick={() => setActiveCat(c.slug)}
                data-testid={`guides-cat-${c.slug}`}
                className={`giwahs-tab-gold ${activeCat === c.slug ? 'active' : ''}`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      {activeCat === 'all' && !query && featured && (
        <section className="border-b border-hairline surface-obsidian-2">
          <div className="mx-auto max-w-[1440px] px-5 py-12 md:px-8 md:py-16">
            <span className="label-overline">/ Featured Guide</span>
            <Link to={`/sourcing-guides/${featured.slug}`} className="mt-6 grid gap-8 md:grid-cols-12" data-testid="guides-featured">
              <div className="aspect-[16/10] overflow-hidden border border-black md:col-span-7">
                <Img src={featured.cover} alt={`${featured.title} — featured sourcing guide`} variant="heroNarrow" priority className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
              </div>
              <div className="md:col-span-5 flex flex-col justify-end">
                <span className="label-overline giwahs-chip-dark w-fit">{featured.categoryLabel}</span>
                <h2 className="mt-5 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2rem, 4.5vw, 4rem)', lineHeight: 0.95 }}>
                  {featured.title}
                </h2>
                <p className="mt-4 text-base leading-relaxed text-neutral-700">{featured.summary}</p>
                <div className="mt-5 flex items-center gap-4 font-mono-data text-xs text-neutral-600">
                  <span className="flex items-center gap-1.5"><Clock size={12} strokeWidth={1.5} /> {featured.readingMin} min read</span>
                  <span>·</span>
                  <span>{featured.author}</span>
                </div>
                <span className="mt-7 inline-flex items-center gap-1 label-overline border-b border-black pb-0.5 w-fit">
                  Read intelligence brief <ArrowUpRight size={12} strokeWidth={1.5} />
                </span>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* GRID */}
      <section className="surface-obsidian text-platinum">
        <div className="mx-auto max-w-[1440px] px-5 py-14 md:px-8 md:py-20">
          <div className="mb-6 flex items-center justify-between border-b border-neutral-200 pb-3">
            <span className="font-mono-data text-xs text-neutral-600" data-testid="guides-result-count">
              {loading ? 'LOADING…' : `${filtered.length} GUIDES`}
            </span>
            <span className="font-mono-data text-xs text-neutral-400">EDITORIAL · 2026</span>
          </div>
          {loading && <LoadingState label="Loading guides…" />}
          {error && !loading && <ErrorState description={error?.message || 'Failed to load guides.'} onRetry={refetch} />}
          {!loading && !error && filtered.length === 0 && (
            <EmptyState title="No guides match" description="Try clearing your search or selecting a different category." />
          )}
          {!loading && !error && filtered.length > 0 && (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((g, i) => (
                <Link
                  key={g.slug}
                  to={`/sourcing-guides/${g.slug}`}
                  data-testid={`guide-card-${g.slug}`}
                  className="group flex flex-col border border-black bg-white reveal-up"
                  style={{ animationDelay: `${i * 50}ms` }}
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-neutral-100">
                    <Img src={g.cover} alt={`${g.title} — sourcing guide cover`} variant="card" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <span className="label-overline giwahs-chip-ink w-fit">{g.categoryLabel}</span>
                    <h3 className="mt-3 font-display text-xl leading-tight tracking-tight md:text-2xl" style={{ fontWeight: 600 }}>{g.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-neutral-700">{g.summary}</p>
                    <div className="mt-auto pt-5 flex items-center justify-between border-t border-neutral-200 font-mono-data text-xs text-neutral-600">
                      <span className="flex items-center gap-1.5"><Clock size={12} strokeWidth={1.5} /> {g.readingMin} min</span>
                      <span className="inline-flex items-center gap-1 label-overline text-black">Read <ArrowUpRight size={11} strokeWidth={1.5} /></span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
      <section className="border-y border-black bg-[#F5F5F0]">
        <div className="mx-auto max-w-[1440px] grid gap-12 px-5 py-14 md:grid-cols-3 md:px-8 md:py-20">
          <div>
            <div className="label-overline">By Category</div>
            <ul className="mt-5 space-y-2.5 text-sm">
              <li><Link to="/category/streetwear-manufacturers" className="hover:underline">Streetwear Manufacturers →</Link></li>
              <li><Link to="/category/cap-manufacturers" className="hover:underline">Cap Manufacturers →</Link></li>
              <li><Link to="/category/activewear-factories" className="hover:underline">Activewear Factories →</Link></li>
              <li><Link to="/category/workwear-suppliers" className="hover:underline">Workwear Suppliers →</Link></li>
              <li><Link to="/category/denim-manufacturers" className="hover:underline">Denim Manufacturers →</Link></li>
              <li><Link to="/category/footwear-factories" className="hover:underline">Footwear Factories →</Link></li>
            </ul>
          </div>
          <div>
            <div className="label-overline">By Country</div>
            <ul className="mt-5 space-y-2.5 text-sm">
              <li><Link to="/manufacturers/portugal" className="hover:underline">Manufacturers in Portugal →</Link></li>
              <li><Link to="/manufacturers/turkey" className="hover:underline">Manufacturers in Turkey →</Link></li>
              <li><Link to="/manufacturers/china" className="hover:underline">Manufacturers in China →</Link></li>
              <li><Link to="/manufacturers/vietnam" className="hover:underline">Manufacturers in Vietnam →</Link></li>
              <li><Link to="/manufacturers/india" className="hover:underline">Manufacturers in India →</Link></li>
              <li><Link to="/manufacturers/bangladesh" className="hover:underline">Manufacturers in Bangladesh →</Link></li>
              <li><Link to="/manufacturers/pakistan" className="hover:underline">Manufacturers in Pakistan →</Link></li>
            </ul>
          </div>
          <div>
            <div className="label-overline">Reference</div>
            <ul className="mt-5 space-y-2.5 text-sm">
              <li><Link to="/trust-score" className="hover:underline">How Trust Score works →</Link></li>
              <li><Link to="/intelligence" className="hover:underline">Brand × Factory Intelligence →</Link></li>
              <li><Link to="/why-giwahs" className="hover:underline">Why Giwahs →</Link></li>
              <li><Link to="/for-suppliers" className="hover:underline">For Suppliers →</Link></li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SourcingGuides;
